import { ReactNode, useMemo } from 'react';

import AlertIcon from '@/assets/icons/alert.svg';
import AlertWarningIcon from '@/assets/icons/alert_warning.svg';
import AlertSuccessIcon from '@/assets/icons/alert_success.svg';

import './styles.css';

type Variant = 'error' | 'warning' | 'info' | 'success';

interface IProps {
    children?: ReactNode;
    label: ReactNode;
    boldLabel?: boolean;
    biggerLabel?: boolean;
    className?: string;
    labelClassName?: string;
    childrenClassName?: string;
    testId?: string;
    variant?: Variant;
    withIcon?: boolean;
    avoidAlertDetection?: boolean;
}

interface Style {
    bg: string;
    border: string;
    icon?: string;
    text: string;
    children?: string;
}

const styles: { [key in Variant]: Style } = {
    error: {
        bg: 'bg-red-300',
        border: 'bg-red-500',
        icon: AlertIcon,
        text: 'text-red-500',
        children: 'rounded-10 bg-alertErrorChildren p-4'
    },
    warning: {
        bg: 'bg-yellow-100',
        border: 'bg-yellow-300',
        icon: AlertWarningIcon,
        text: 'text-dark'
    },
    info: {
        bg: 'bg-mint-100',
        border: 'bg-mint-400',
        text: 'text-primary-400'
    },
    success: {
        bg: 'bg-mint-200',
        border: 'bg-primary-700',
        text: 'text-black',
        icon: AlertSuccessIcon,
        children: 'px-5'
    }
};

export const Alert = ({
    children,
    label,
    boldLabel = true,
    className = '',
    biggerLabel = false,
    labelClassName = '',
    childrenClassName = '',
    variant = 'error',
    testId = 'alert',
    withIcon = true,
    avoidAlertDetection = false
}: IProps) => {
    const style = useMemo(() => styles[variant], [variant]);

    return (
        <div
            className={`${avoidAlertDetection ? '' : `alert__${variant}`} relative flex w-full flex-col gap-2 overflow-hidden rounded-lg p-5 ${style.bg} ${className}`}
            data-testid={testId}
            role="alert"
        >
            <div className={`cutted absolute left-0 top-0 h-full w-1.5 ${style.border}`}></div>
            <div className="flex items-center gap-2">
                {withIcon && <img className="h-5 w-5" alt="Alert icon" src={style.icon} />}
                <span
                    data-testid={`${testId}-label`}
                    className={`${style.text} ${boldLabel ? 'font-bold' : 'font-normal'} ${biggerLabel ? 'text-lg' : 'text-base'} ${labelClassName}`}
                >
                    {label}
                </span>
            </div>
            {children && <div data-testid={`${testId}-children`} className={(childrenClassName || style.children || '') + ''}>{children}</div>}
        </div>
    );
};

export default Alert;
